import { 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  sendPasswordResetEmail,
  updateProfile,
  onAuthStateChanged,
  getAuth
} from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { auth, firebaseConfig } from '../config/firebase';
import { userService } from './userService';
import { 
  USER_STATUS, 
  USER_ROLES,
  AUTH_ERROR_MESSAGES
} from '../constants/authConstants';

class AuthService {
  constructor() {
    this.currentUser = null;
    this.authStateListeners = [];
    this.isSigningUp = false;
    this.pendingSignupUser = null;
    this.secondaryApp = null;
    this.secondaryAuth = null;
  }

  initializeSecondaryApp() {
    if (!this.secondaryApp) {
      try {
        this.secondaryApp = initializeApp(firebaseConfig, 'Secondary');
        this.secondaryAuth = getAuth(this.secondaryApp);
        console.log('Secondary Firebase app initialized');
      } catch (error) {
        console.error('Error initializing secondary app:', error);
      }
    }
    return this.secondaryAuth;
  }

  initAuthListener() {
    return onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const userData = await userService.getUserById(user.uid);
          
          if (this.isSigningUp && this.pendingSignupUser?.uid === user.uid) {
            console.log('Auth listener: User created during signup, will sign out after document creation');
            return;
          }
          
          if (userData.status !== USER_STATUS.ACTIVE) {
            console.log('User account is not active:', userData.status);
            await signOut(auth);
            this.currentUser = null;
            this.authStateListeners.forEach(listener => listener(null));
            return;
          }

          this.currentUser = {
            uid: user.uid,
            email: user.email,
            emailVerified: user.emailVerified,
            ...userData
          };
        } catch (error) {
          console.error('Error fetching user data:', error);
          await signOut(auth);
          this.currentUser = null;
        }
      } else {
        this.currentUser = null;
      }
      
      this.authStateListeners.forEach(listener => listener(this.currentUser));
    });
  }

  onAuthStateChange(callback) {
    this.authStateListeners.push(callback);
    
    return () => {
      const index = this.authStateListeners.indexOf(callback);
      if (index > -1) {
        this.authStateListeners.splice(index, 1);
      }
    };
  }

  async signUp(email, password, userData) {
    try {
      this.isSigningUp = true;
      
      console.log('Checking email uniqueness...');
      
      const existingUser = await userService.getUserByEmail(email);
      if (existingUser) {
        throw new Error(AUTH_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS);
      }

      console.log('Creating Firebase auth user...');
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      this.pendingSignupUser = user;

      console.log('Updating user profile...');
      await updateProfile(user, {
        displayName: userData.fullName
      });

      console.log('Creating user document...');
      
      const userDoc = {
        uid: user.uid,
        email: user.email,
        fullName: userData.fullName,
        sector: userData.sector,
        department: userData.department,
        role: USER_ROLES.DEPARTMENT,
        profilePicture: null,
        status: USER_STATUS.PENDING,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: null,
        approvedAt: null,
        approvedBy: null
      };

      await userService.createUser(user.uid, userDoc);
      
      console.log('Signing out user - account pending approval');
      await signOut(auth);
      
      this.isSigningUp = false;
      this.pendingSignupUser = null;
      
      console.log('User created successfully');
      return {
        user: userDoc,
        message: 'Registration successful! Your account is pending approval.',
        status: 'pending_approval'
      };
    } catch (error) {
      console.error('Sign up error:', error);
      
      this.isSigningUp = false;
      this.pendingSignupUser = null;
      
      if (error.message.includes('already exists')) {
        throw error;
      }
      
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  async signIn(email, password) {
    try {
      console.log('Checking user status before sign in...');
      
      const userData = await userService.getUserByEmail(email);
      if (userData) {
        switch (userData.status) {
          case USER_STATUS.PENDING:
            throw new Error('Your account is pending admin approval. You will receive an email once your account is activated.');
          case USER_STATUS.INACTIVE:
            throw new Error('Your account is inactive. Please contact the administrator to reactivate your account.');
          case USER_STATUS.SUSPENDED:
            throw new Error('Your account has been suspended. Please contact the administrator.');
          case USER_STATUS.ACTIVE:
            break;
          default:
            throw new Error('Your account status is unknown. Please contact the administrator.');
        }
      } else {
        throw new Error('No account found with this email address. Please sign up first.');
      }

      console.log('Signing in user...');
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      console.log('Updating last login time...');
      await userService.updateUserLastLogin(user.uid);

      console.log('User signed in successfully');
      return {
        user: {
          uid: user.uid,
          email: user.email,
          emailVerified: user.emailVerified
        }
      };
    } catch (error) {
      console.error('Sign in error:', error);
      
      if (error.message.includes('account') || error.message.includes('pending') || error.message.includes('suspended')) {
        throw error;
      }
      
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  async signOut() {
    try {
      await signOut(auth);
      this.currentUser = null;
      console.log('User signed out successfully');
    } catch (error) {
      console.error('Sign out error:', error);
      throw new Error('Failed to sign out');
    }
  }

  async resetPassword(email) {
    try {
      const userData = await userService.getUserByEmail(email);
      if (!userData) {
        throw new Error('No account found with this email address');
      }

      if (userData.status === USER_STATUS.PENDING) {
        throw new Error('Your account is pending approval. Password reset is not available until your account is activated.');
      } else if (userData.status !== USER_STATUS.ACTIVE) {
        throw new Error('Password reset is only available for active accounts. Please contact administrator.');
      }

      await sendPasswordResetEmail(auth, email);
      console.log('Password reset email sent');
    } catch (error) {
      console.error('Password reset error:', error);
      
      if (error.message.includes('account') || error.message.includes('pending') || error.message.includes('No account found')) {
        throw error;
      }
      
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  generateTemporaryPassword() {
    const length = 16;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*';
    let password = '';
    
    password += 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'[Math.floor(Math.random() * 26)];
    password += 'abcdefghijklmnopqrstuvwxyz'[Math.floor(Math.random() * 26)];
    password += '0123456789'[Math.floor(Math.random() * 10)];
    password += '!@#$%^&*'[Math.floor(Math.random() * 8)];
    
    for (let i = password.length; i < length; i++) {
      password += charset[Math.floor(Math.random() * charset.length)];
    }
    
    return password.split('').sort(() => Math.random() - 0.5).join('');
  }

  async adminCreateUser(userData) {
    try {
      console.log('Admin creating user...');
      
      if (!this.isAdmin() && !this.isPlanningOfficial()) {
        throw new Error(AUTH_ERROR_MESSAGES.UNAUTHORIZED);
      }

      const existingUser = await userService.getUserByEmail(userData.email);
      if (existingUser) {
        throw new Error(AUTH_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS);
      }

      const secondaryAuth = this.initializeSecondaryApp();
      if (!secondaryAuth) {
        throw new Error('Failed to initialize secondary authentication');
      }

      const tempPassword = this.generateTemporaryPassword();

      console.log('Creating Firebase auth user with secondary app...');
      
      const userCredential = await createUserWithEmailAndPassword(secondaryAuth, userData.email, tempPassword);
      const user = userCredential.user;

      console.log('Updating new user profile...');
      await updateProfile(user, {
        displayName: userData.fullName
      });

      console.log('Creating user document in Firestore...');
      
      const userDoc = {
        uid: user.uid,
        email: user.email,
        fullName: userData.fullName,
        sector: userData.sector || null,
        department: userData.department || null,
        role: userData.role,
        profilePicture: null,
        status: userData.status,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: null,
        createdBy: this.currentUser.uid,
        createdByAdmin: true
      };

      await userService.createUser(user.uid, userDoc);

      console.log('Sending password reset email...');
      await sendPasswordResetEmail(auth, userData.email);
      
      console.log('Signing out newly created user from secondary auth...');
      await signOut(secondaryAuth);
      
      console.log('User created successfully by admin');
      
      return {
        user: userDoc,
        message: 'User created successfully! Password reset email sent.',
        status: 'success'
      };
    } catch (error) {
      console.error('Admin user creation error:', error);
      
      if (error.message.includes('already exists') || error.message.includes('UNAUTHORIZED')) {
        throw error;
      }
      
      throw new Error(this.getErrorMessage(error.code));
    }
  }

  getCurrentUser() {
    return this.currentUser;
  }

  isAuthenticated() {
    return !!this.currentUser;
  }

  getUserRole() {
    return this.currentUser?.role || null;
  }

  isAdmin() {
    return this.currentUser?.role === USER_ROLES.ADMIN;
  }

  isDepartment() {
    return this.currentUser?.role === USER_ROLES.DEPARTMENT;
  }

  isPlanningOfficial() {
    return this.currentUser?.role === USER_ROLES.PLANNING_OFFICIAL;
  }

  isDoner() {
    return this.currentUser?.role === USER_ROLES.DONER;
  }

  // Helper: Check if user has admin-like access
  hasAdminAccess() {
    return this.isAdmin() || this.isPlanningOfficial() || this.isDoner();
  }

  getUserDisplayName() {
    if (!this.currentUser) return '';
    return this.currentUser.fullName || this.currentUser.email || 'User';
  }

  hasPermission(permission) {
    if (!this.currentUser) return false;
    
    const { ROLE_PERMISSIONS } = require('../constants/authConstants');
    const userPermissions = ROLE_PERMISSIONS[this.currentUser.role] || [];
    return userPermissions.includes(permission);
  }

  updateCurrentUserData(newData) {
    if (this.currentUser) {
      this.currentUser = { ...this.currentUser, ...newData };
      this.authStateListeners.forEach(listener => listener(this.currentUser));
    }
  }

  async refreshCurrentUser() {
    if (!this.currentUser) return null;

    try {
      const userData = await userService.getUserById(this.currentUser.uid);
      
      this.currentUser = {
        ...this.currentUser,
        ...userData
      };
      
      this.authStateListeners.forEach(listener => listener(this.currentUser));
      
      return this.currentUser;
    } catch (error) {
      console.error('Error refreshing current user:', error);
      return this.currentUser;
    }
  }

  async approveUser(userId) {
    try {
      if (!this.isAdmin()) {
        throw new Error(AUTH_ERROR_MESSAGES.UNAUTHORIZED);
      }

      await userService.updateUser(userId, {
        status: USER_STATUS.ACTIVE,
        approvedAt: new Date(),
        approvedBy: this.currentUser.uid
      });

      console.log('User approved successfully');
      return { message: 'User approved successfully' };
    } catch (error) {
      console.error('User approval error:', error);
      throw error;
    }
  }

  async updateUserStatus(userId, newStatus) {
    try {
      if (!this.isAdmin()) {
        throw new Error(AUTH_ERROR_MESSAGES.UNAUTHORIZED);
      }

      await userService.updateUserStatus(userId, newStatus);

      console.log(`User status changed to ${newStatus}`);
      return { message: `User ${newStatus} successfully` };
    } catch (error) {
      console.error('User status change error:', error);
      throw error;
    }
  }

  async deleteUser(userId) {
    try {
      if (!this.isAdmin()) {
        throw new Error(AUTH_ERROR_MESSAGES.UNAUTHORIZED);
      }

      if (userId === this.currentUser.uid) {
        throw new Error('Cannot delete your own account');
      }

      await userService.deleteUser(userId);
      console.log('User deleted successfully');
      return { message: 'User deleted successfully' };
    } catch (error) {
      console.error('User deletion error:', error);
      throw error;
    }
  }

  async getAllUsers() {
    try {
      // Allow both Admin and Planning Official to view users
      if (!this.hasAdminAccess()) {
        throw new Error(AUTH_ERROR_MESSAGES.UNAUTHORIZED);
      }

      return await userService.getAllUsers();
    } catch (error) {
      console.error('Error getting all users:', error);
      throw error;
    }
  }

  clearCache() {
    this.currentUser = null;
  }

  getErrorMessage(errorCode) {
    const errorMessages = {
      'auth/user-not-found': 'No account found with this email address',
      'auth/wrong-password': 'Incorrect password',
      'auth/email-already-in-use': AUTH_ERROR_MESSAGES.EMAIL_ALREADY_EXISTS,
      'auth/weak-password': AUTH_ERROR_MESSAGES.WEAK_PASSWORD,
      'auth/invalid-email': AUTH_ERROR_MESSAGES.INVALID_EMAIL,
      'auth/user-disabled': 'This account has been disabled',
      'auth/too-many-requests': 'Too many failed attempts. Please try again later',
      'auth/network-request-failed': 'Network error. Please check your connection',
      'auth/invalid-credential': AUTH_ERROR_MESSAGES.INVALID_CREDENTIALS,
      'auth/user-token-expired': AUTH_ERROR_MESSAGES.SESSION_EXPIRED,
    };

    return errorMessages[errorCode] || 'An unexpected error occurred. Please try again.';
  }
}

export const authService = new AuthService();